import Select, {components} from 'react-select';
import React, { useState, useEffect } from 'react';

const MultipleSelect = ({ placeholderText, selectOptions, selectedOptions, selectedValue, handleChange, required, customStyles, isMultiSelect, label, error }) => {
  
  // Add a more visible debug log to check options
  useEffect(() => {
    // Make this log stand out
    console.log("%c==== MultipleSelect Debug ====", "background: #4CAF50; color: white; font-size: 12px; padding: 4px;");
    console.log(`Rendering ${placeholderText} dropdown`);
    console.log("Options count:", selectOptions?.length || 0);
    console.log("Raw options:", selectOptions);
    console.log("Is multi-select:", isMultiSelect);
    console.log("%c============================", "background: #4CAF50; color: white; font-size: 12px; padding: 4px;");
    
    // Existing detailed debugging
    if (selectOptions) {
      // Look for options with id or value of 0
      const zeroOptions = selectOptions.filter(option => 
        option && (option.value === 0 || option.id === 0));
      
      if (zeroOptions.length > 0) {
        console.warn("Found options with value 0:", zeroOptions);
      }
      
      // Look for options with empty/invalid labels
      const invalidLabelOptions = selectOptions.filter(option => 
        option && (!option.label || option.label === '' || option.label === 'undefined'));
      
      if (invalidLabelOptions.length > 0) {
        console.warn("Found options with invalid labels:", invalidLabelOptions);
      }
    }
  }, [selectOptions, selectedOptions, placeholderText, isMultiSelect]);

  // Process the options with more detailed checks
  const safeOptions = React.useMemo(() => {
    if (!selectOptions || !Array.isArray(selectOptions)) {
      console.warn("selectOptions is not a valid array:", selectOptions);
      return [];
    }
    
    const processed = selectOptions
      .filter(option => {
        // Must have a valid option object
        if (!option) {
          console.warn("Filtering out null/undefined option");
          return false;
        }
        
        // Extra check to filter out any option with value 0
        if (option.value === 0) {
          console.warn("Filtering out option with value 0:", option);
          return false;
        }
        
        // Must have a valid label (must be non-empty string)
        if (!option.label || typeof option.label !== 'string' || option.label.trim() === '') {
          console.warn("Filtering out option with invalid label:", option);
          return false;
        }
        
        return true;
      })
      .map(option => ({
        ...option,
        // Ensure label is properly formatted
        label: String(option.label).trim()
      }));
    
    console.log(`Processed ${processed.length} options from ${selectOptions.length} original options`);
    return processed;
  }, [selectOptions]);

  // Enhanced custom option to support showing email addresses
  const CustomOption = props => {
    // Don't render options with value 0 or empty labels
    if (props.data.value === 0 || !props.data.label) {
      return null;
    }
    
    // Check if this option has an email property
    const hasEmail = props.data.email && props.data.email !== props.data.label;
    
    return (
      <components.Option {...props}>
        {hasEmail ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 500 }}>{props.data.label}</div>
            <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '2px' }}>{props.data.email}</small>
          </div>
        ) : (
          props.children
        )}
      </components.Option>
    );
  };
  
  // Custom value container to prevent "0" from showing
  const CustomValueContainer = props => {
    // Filter out any zero values from children
    const children = React.Children.toArray(props.children).filter(child => {
      if (React.isValidElement(child) && child.props.data && child.props.data.value === 0) {
        return false;
      }
      return true;
    });
    
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  };

  return (
    <Select
      aria-label={label} // Set aria-label dynamically based on the 'label' prop
      options={safeOptions}
      defaultValue={selectedValue}
      isMulti={isMultiSelect}
      className="basic-multi-select"
      classNamePrefix="select"
      menuPlacement="bottom"
      placeholder={placeholderText}
      value={selectedOptions}
      onChange={handleChange}
      noOptionsMessage={() => "No options available"}
      components={{ 
        Option: CustomOption,
        ValueContainer: CustomValueContainer
      }}
      hideSelectedOptions={false}
      styles={{
        ...customStyles,
        control: (provided, state) => ({
          ...provided,
          minHeight: '50px', 
          height: 'auto',
          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : null,
          borderColor: error ? '#d32f2f' : provided.borderColor,
          '&:hover': {
            borderColor: error ? '#d32f2f' : state.isFocused ? 'rgba(0,123,255,.25)' : provided.borderColor,
          }
        }),
        menu: (provided) => ({
          ...provided,
          maxHeight: '400px',
          fontSize: '14px', 
          zIndex: 9999,
        }),
        option: (provided) => ({
          ...provided,
          fontSize: '14px',
          color: '#333',  // Ensure text is visible
          padding: '8px 12px',
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: '#f0f7ff'
          }
        }),
        singleValue: (provided) => ({
          ...provided,
          fontSize: '14px',
          color: '#333'  // Ensure text is visible
        }),
        multiValue: (provided) => ({
          ...provided,
          fontSize: '14px',
          backgroundColor: '#e8f4fd',
          borderRadius: '4px',
          color: '#333'  // Ensure text is visible
        }),
        multiValueLabel: (provided) => ({
          ...provided,
          color: '#333', // Ensure text is visible
          fontWeight: 500,
          padding: '2px 4px'
        }),
        placeholder: (provided) => ({  
          ...provided,
          fontSize: '14px',
          color: '#aaa'  // Lighter color for placeholder
        }),
        // Fix for the zero value showing at the bottom
        indicatorsContainer: (provided) => ({
          ...provided,
          height: 'auto',
          '& > div': {
            padding: '4px'
          }
        }),
        valueContainer: (provided) => ({
          ...provided,
          padding: '4px 8px',
          overflow: 'visible'
        }),
        // Remove any numbers showing at the bottom
        input: (provided) => ({
          ...provided,
          padding: 0,
          margin: 0,
          '& input': { opacity: 1 }
        })
      }}
      aria-required={required} 
      required={required}
      aria-invalid={error ? 'true' : 'false'}
    />
  );
}

export default MultipleSelect;